<template>
  <ion-page>
    <ion-content class="h-full">
      <round-header
        image-type="emoji"
        image="🤕"
        title="Headache Tracker"
        subtitle="Toolbox"
        back="/tools"
        disable-edit-mode="true"
      >
      </round-header>

      <div class="flex justify-center">
        <div class="w-full max-w-[1024px] p-4 px-8 md:px-20">
          <!-- Error message -->
          <div v-if="error" class="mb-4 rounded-lg bg-red-100 p-4 text-red-700">
            {{ error }}
          </div>
          <div class="rounded-2xl bg-[#ffffff44] p-6 text-center shadow-sm">
            <h2 class="font-fredoka font-outline-1 mb-4 text-xl text-white">Where does it hurt?</h2>
            <div class="flex flex-col items-center">
              <svg id="hs" width="100%" viewBox="0 0 680 400" role="img" class="max-w-[800px]">
                <title>Dual head headache selector</title>
                <text
                  x="170"
                  y="24"
                  text-anchor="middle"
                  class="head-label font-outline-1 fill-white font-sans text-sm font-medium"
                >
                  Front
                </text>
                <text
                  x="510"
                  y="24"
                  text-anchor="middle"
                  class="head-label font-outline-1 fill-white font-sans text-sm font-medium"
                >
                  Back
                </text>

                <!-- FRONT HEAD -->
                <path
                  d="M148 328 C146 345 143 360 141 375 L199 375 C197 360 194 345 192 328 Z"
                  fill="#e8d5c4"
                  stroke="#c4a882"
                  stroke-width="0.5"
                />
                <ellipse
                  cx="102"
                  cy="238"
                  rx="13"
                  ry="20"
                  fill="#e8d5c4"
                  stroke="#c4a882"
                  stroke-width="0.5"
                />
                <ellipse
                  cx="238"
                  cy="238"
                  rx="13"
                  ry="20"
                  fill="#e8d5c4"
                  stroke="#c4a882"
                  stroke-width="0.5"
                />
                <path
                  d="M118 310 C104 305 96 286 95 270 L92 196 C90 138 110 90 170 83 C230 90 250 138 248 196 L245 270 C244 286 236 305 222 310 Z"
                  fill="#f5e6d8"
                  stroke="#c4a882"
                  stroke-width="0.8"
                />

                <!-- FRONT ZONES -->
                <g
                  class="cursor-pointer transition-opacity duration-150"
                  @click="toggleZone('crown')"
                >
                  <path
                    d="M128 97 C138 82 158 78 170 77 C182 78 202 82 212 97 L218 135 L122 135 Z"
                    :fill="zoneColors['crown']"
                    :fill-opacity="isZoneActive('crown') ? 0.52 : 0.13"
                  />
                  <path
                    d="M128 97 C138 82 158 78 170 77 C182 78 202 82 212 97 L218 135 L122 135 Z"
                    fill="none"
                    :stroke="zoneColors['crown']"
                    :stroke-width="isZoneActive('crown') ? 2 : 1"
                    :stroke-dasharray="isZoneActive('crown') ? 'none' : '4,3'"
                    :opacity="isZoneActive('crown') ? 1 : 0.5"
                  />
                </g>
                <g
                  class="cursor-pointer transition-opacity duration-150"
                  @click="toggleZone('forehead')"
                >
                  <rect
                    x="122"
                    y="135"
                    width="96"
                    height="55"
                    rx="3"
                    :fill="zoneColors['forehead']"
                    :fill-opacity="isZoneActive('forehead') ? 0.52 : 0.12"
                  />
                  <rect
                    x="122"
                    y="135"
                    width="96"
                    height="55"
                    rx="3"
                    fill="none"
                    :stroke="zoneColors['forehead']"
                    :stroke-width="isZoneActive('forehead') ? 2 : 1"
                    :stroke-dasharray="isZoneActive('forehead') ? 'none' : '4,3'"
                    :opacity="isZoneActive('forehead') ? 1 : 0.45"
                  />
                </g>
                <g
                  class="cursor-pointer transition-opacity duration-150"
                  @click="toggleZone('crown-temple-l')"
                >
                  <path
                    d="M125 116 L106 142 L122 135 Z"
                    :fill="zoneColors['crown-temple-l']"
                    :fill-opacity="isZoneActive('crown-temple-l') ? 0.52 : 0.12"
                  />
                  <path
                    d="M125 116 L106 142 L122 135 Z"
                    fill="none"
                    :stroke="zoneColors['crown-temple-l']"
                    :stroke-width="isZoneActive('crown-temple-l') ? 2 : 1"
                    :stroke-dasharray="isZoneActive('crown-temple-l') ? 'none' : '4,3'"
                    :opacity="isZoneActive('crown-temple-l') ? 1 : 0.45"
                  />
                </g>
                <g
                  class="cursor-pointer transition-opacity duration-150"
                  @click="toggleZone('crown-temple-r')"
                >
                  <path
                    d="M215 116 L234 142 L218 135 Z"
                    :fill="zoneColors['crown-temple-r']"
                    :fill-opacity="isZoneActive('crown-temple-r') ? 0.52 : 0.12"
                  />
                  <path
                    d="M215 116 L234 142 L218 135 Z"
                    fill="none"
                    :stroke="zoneColors['crown-temple-r']"
                    :stroke-width="isZoneActive('crown-temple-r') ? 2 : 1"
                    :stroke-dasharray="isZoneActive('crown-temple-r') ? 'none' : '4,3'"
                    :opacity="isZoneActive('crown-temple-r') ? 1 : 0.45"
                  />
                </g>
                <g
                  class="cursor-pointer transition-opacity duration-150"
                  @click="toggleZone('temple-l')"
                >
                  <path
                    d="M122 135 L90 150 L92 215 L108 222 L118 190 L122 190 Z"
                    :fill="zoneColors['temple-l']"
                    :fill-opacity="isZoneActive('temple-l') ? 0.52 : 0.12"
                  />
                  <path
                    d="M122 135 L90 150 L92 215 L108 222 L118 190 L122 190 Z"
                    fill="none"
                    :stroke="zoneColors['temple-l']"
                    :stroke-width="isZoneActive('temple-l') ? 2 : 1"
                    :stroke-dasharray="isZoneActive('temple-l') ? 'none' : '4,3'"
                    :opacity="isZoneActive('temple-l') ? 1 : 0.45"
                  />
                </g>
                <g
                  class="cursor-pointer transition-opacity duration-150"
                  @click="toggleZone('temple-r')"
                >
                  <path
                    d="M218 135 L250 150 L248 215 L232 222 L222 190 L218 190 Z"
                    :fill="zoneColors['temple-r']"
                    :fill-opacity="isZoneActive('temple-r') ? 0.52 : 0.12"
                  />
                  <path
                    d="M218 135 L250 150 L248 215 L232 222 L222 190 L218 190 Z"
                    fill="none"
                    :stroke="zoneColors['temple-r']"
                    :stroke-width="isZoneActive('temple-r') ? 2 : 1"
                    :stroke-dasharray="isZoneActive('temple-r') ? 'none' : '4,3'"
                    :opacity="isZoneActive('temple-r') ? 1 : 0.45"
                  />
                </g>
                <g
                  class="cursor-pointer transition-opacity duration-150"
                  @click="toggleZone('sinus-l')"
                >
                  <path
                    d="M122 190 L118 190 L108 222 L120 244 L140 247 L147 222 L143 190 Z"
                    :fill="zoneColors['sinus-l']"
                    :fill-opacity="isZoneActive('sinus-l') ? 0.52 : 0.12"
                  />
                  <path
                    d="M122 190 L118 190 L108 222 L120 244 L140 247 L147 222 L143 190 Z"
                    fill="none"
                    :stroke="zoneColors['sinus-l']"
                    :stroke-width="isZoneActive('sinus-l') ? 2 : 1"
                    :stroke-dasharray="isZoneActive('sinus-l') ? 'none' : '4,3'"
                    :opacity="isZoneActive('sinus-l') ? 1 : 0.45"
                  />
                </g>
                <g
                  class="cursor-pointer transition-opacity duration-150"
                  @click="toggleZone('sinus-r')"
                >
                  <path
                    d="M218 190 L222 190 L232 222 L220 244 L200 247 L193 222 L197 190 Z"
                    :fill="zoneColors['sinus-r']"
                    :fill-opacity="isZoneActive('sinus-r') ? 0.52 : 0.12"
                  />
                  <path
                    d="M218 190 L222 190 L232 222 L220 244 L200 247 L193 222 L197 190 Z"
                    fill="none"
                    :stroke="zoneColors['sinus-r']"
                    :stroke-width="isZoneActive('sinus-r') ? 2 : 1"
                    :stroke-dasharray="isZoneActive('sinus-r') ? 'none' : '4,3'"
                    :opacity="isZoneActive('sinus-r') ? 1 : 0.45"
                  />
                </g>

                <!-- Face features -->
                <ellipse
                  cx="150"
                  cy="205"
                  rx="14"
                  ry="8"
                  fill="white"
                  stroke="#c4a882"
                  stroke-width="0.5"
                />
                <ellipse
                  cx="190"
                  cy="205"
                  rx="14"
                  ry="8"
                  fill="white"
                  stroke="#c4a882"
                  stroke-width="0.5"
                />
                <circle cx="150" cy="205" r="4.5" fill="#5a3e2b" />
                <circle cx="190" cy="205" r="4.5" fill="#5a3e2b" />
                <circle cx="152" cy="203" r="1.5" fill="white" />
                <circle cx="192" cy="203" r="1.5" fill="white" />
                <path
                  d="M137 195 Q150 189 163 192"
                  fill="none"
                  stroke="#8b6748"
                  stroke-width="1.2"
                  stroke-linecap="round"
                />
                <path
                  d="M177 192 Q190 189 203 195"
                  fill="none"
                  stroke="#8b6748"
                  stroke-width="1.2"
                  stroke-linecap="round"
                />
                <path
                  d="M166 215 L161 238 Q170 244 179 238 L174 215"
                  fill="none"
                  stroke="#c4a882"
                  stroke-width="0.8"
                  stroke-linecap="round"
                />
                <path
                  d="M158 264 Q170 273 182 264"
                  fill="none"
                  stroke="#c4a882"
                  stroke-width="0.9"
                  stroke-linecap="round"
                />

                <!-- Front labels -->
                <text
                  x="170"
                  y="112"
                  text-anchor="middle"
                  class="pointer-events-none font-sans text-[11px] font-medium"
                  :fill="zoneColors['crown']"
                  opacity="0.75"
                >
                  crown
                </text>
                <text
                  x="170"
                  y="162"
                  text-anchor="middle"
                  class="pointer-events-none font-sans text-[11px] font-medium"
                  :fill="zoneColors['forehead']"
                  opacity="0.7"
                >
                  forehead
                </text>
                <text
                  x="96"
                  y="178"
                  text-anchor="middle"
                  class="pointer-events-none font-sans text-[10px] font-medium"
                  :fill="zoneColors['temple-l']"
                  opacity="0.7"
                >
                  temple
                </text>
                <text
                  x="244"
                  y="178"
                  text-anchor="middle"
                  class="pointer-events-none font-sans text-[10px] font-medium"
                  :fill="zoneColors['temple-r']"
                  opacity="0.7"
                >
                  temple
                </text>
                <text
                  x="118"
                  y="224"
                  text-anchor="middle"
                  class="pointer-events-none font-sans text-[10px] font-medium"
                  :fill="zoneColors['sinus-l']"
                  opacity="0.7"
                >
                  sinus
                </text>
                <text
                  x="222"
                  y="224"
                  text-anchor="middle"
                  class="pointer-events-none font-sans text-[10px] font-medium"
                  :fill="zoneColors['sinus-r']"
                  opacity="0.7"
                >
                  sinus
                </text>

                <!-- Crown-Temple labels -->
                <text
                  x="108"
                  y="124"
                  text-anchor="middle"
                  class="pointer-events-none font-sans text-[9px] font-medium"
                  :fill="zoneColors['crown-temple-l']"
                  opacity="0.7"
                >
                  side
                </text>
                <text
                  x="232"
                  y="124"
                  text-anchor="middle"
                  class="pointer-events-none font-sans text-[9px] font-medium"
                  :fill="zoneColors['crown-temple-r']"
                  opacity="0.7"
                >
                  side
                </text>

                <!-- BACK HEAD -->
                <path
                  d="M488 328 C486 345 483 360 481 375 L539 375 C537 360 534 345 532 328 Z"
                  fill="#e8d5c4"
                  stroke="#c4a882"
                  stroke-width="0.5"
                />
                <ellipse
                  cx="442"
                  cy="238"
                  rx="13"
                  ry="20"
                  fill="#e8d5c4"
                  stroke="#c4a882"
                  stroke-width="0.5"
                />
                <ellipse
                  cx="578"
                  cy="238"
                  rx="13"
                  ry="20"
                  fill="#e8d5c4"
                  stroke="#c4a882"
                  stroke-width="0.5"
                />
                <path
                  d="M458 310 C444 305 436 286 435 270 L432 196 C430 138 450 88 510 82 C570 88 590 138 588 196 L585 270 C584 286 576 305 562 310 Z"
                  fill="#edddd0"
                  stroke="#c4a882"
                  stroke-width="0.8"
                />
                <path
                  d="M460 88 Q510 76 560 88"
                  fill="none"
                  stroke="#c4a882"
                  stroke-width="0.5"
                  opacity="0.4"
                />

                <!-- BACK ZONES -->
                <g
                  class="cursor-pointer transition-opacity duration-150"
                  @click="toggleZone('occiput-top')"
                >
                  <path
                    d="M458 100 C470 84 492 78 510 77 C528 78 550 84 562 100 L568 160 L452 160 Z"
                    :fill="zoneColors['occiput-top']"
                    :fill-opacity="isZoneActive('occiput-top') ? 0.52 : 0.13"
                  />
                  <path
                    d="M458 100 C470 84 492 78 510 77 C528 78 550 84 562 100 L568 160 L452 160 Z"
                    fill="none"
                    :stroke="zoneColors['occiput-top']"
                    :stroke-width="isZoneActive('occiput-top') ? 2 : 1"
                    :stroke-dasharray="isZoneActive('occiput-top') ? 'none' : '4,3'"
                    :opacity="isZoneActive('occiput-top') ? 1 : 0.5"
                  />
                </g>
                <g
                  class="cursor-pointer transition-opacity duration-150"
                  @click="toggleZone('occiput-l')"
                >
                  <path
                    d="M452 160 L434 175 L432 250 L450 265 L472 260 L480 200 L480 160 Z"
                    :fill="zoneColors['occiput-l']"
                    :fill-opacity="isZoneActive('occiput-l') ? 0.52 : 0.13"
                  />
                  <path
                    d="M452 160 L434 175 L432 250 L450 265 L472 260 L480 200 L480 160 Z"
                    fill="none"
                    :stroke="zoneColors['occiput-l']"
                    :stroke-width="isZoneActive('occiput-l') ? 2 : 1"
                    :stroke-dasharray="isZoneActive('occiput-l') ? 'none' : '4,3'"
                    :opacity="isZoneActive('occiput-l') ? 1 : 0.45"
                  />
                </g>
                <g
                  class="cursor-pointer transition-opacity duration-150"
                  @click="toggleZone('occiput-r')"
                >
                  <path
                    d="M568 160 L586 175 L588 250 L570 265 L548 260 L540 200 L540 160 Z"
                    :fill="zoneColors['occiput-r']"
                    :fill-opacity="isZoneActive('occiput-r') ? 0.52 : 0.13"
                  />
                  <path
                    d="M568 160 L586 175 L588 250 L570 265 L548 260 L540 200 L540 160 Z"
                    fill="none"
                    :stroke="zoneColors['occiput-r']"
                    :stroke-width="isZoneActive('occiput-r') ? 2 : 1"
                    :stroke-dasharray="isZoneActive('occiput-r') ? 'none' : '4,3'"
                    :opacity="isZoneActive('occiput-r') ? 1 : 0.45"
                  />
                </g>
                <g
                  class="cursor-pointer transition-opacity duration-150"
                  @click="toggleZone('base')"
                >
                  <path
                    d="M480 260 L450 265 L432 280 L435 310 L458 325 L488 335 L510 338 L532 335 L562 325 L585 310 L588 280 L570 265 L548 260 L540 200 L480 200 Z"
                    :fill="zoneColors['base']"
                    :fill-opacity="isZoneActive('base') ? 0.52 : 0.12"
                  />
                  <path
                    d="M480 260 L450 265 L432 280 L435 310 L458 325 L488 335 L510 338 L532 335 L562 325 L585 310 L588 280 L570 265 L548 260 L540 200 L480 200 Z"
                    fill="none"
                    :stroke="zoneColors['base']"
                    :stroke-width="isZoneActive('base') ? 2 : 1"
                    :stroke-dasharray="isZoneActive('base') ? 'none' : '4,3'"
                    :opacity="isZoneActive('base') ? 1 : 0.45"
                  />
                </g>
                <g
                  class="cursor-pointer transition-opacity duration-150"
                  @click="toggleZone('neck')"
                >
                  <path
                    d="M488 328 C486 345 483 360 481 375 L539 375 C537 360 534 345 532 328 Z"
                    :fill="zoneColors['neck']"
                    :fill-opacity="isZoneActive('neck') ? 0.52 : 0.18"
                  />
                  <path
                    d="M488 328 C486 345 483 360 481 375 L539 375 C537 360 534 345 532 328 Z"
                    fill="none"
                    :stroke="zoneColors['neck']"
                    :stroke-width="isZoneActive('neck') ? 2 : 1"
                    :stroke-dasharray="isZoneActive('neck') ? 'none' : '4,3'"
                    :opacity="isZoneActive('neck') ? 1 : 0.5"
                  />
                </g>

                <line
                  x1="510"
                  y1="338"
                  x2="510"
                  y2="375"
                  stroke="#c4a882"
                  stroke-width="0.8"
                  opacity="0.4"
                />
                <ellipse
                  cx="442"
                  cy="238"
                  rx="7"
                  ry="13"
                  fill="none"
                  stroke="#c4a882"
                  stroke-width="0.4"
                  opacity="0.5"
                />
                <ellipse
                  cx="578"
                  cy="238"
                  rx="7"
                  ry="13"
                  fill="none"
                  stroke="#c4a882"
                  stroke-width="0.4"
                  opacity="0.5"
                />

                <!-- Back labels -->
                <text
                  x="510"
                  y="124"
                  text-anchor="middle"
                  class="pointer-events-none font-sans text-[11px] font-medium"
                  :fill="zoneColors['occiput-top']"
                  opacity="0.75"
                >
                  upper back
                </text>
                <text
                  x="450"
                  y="216"
                  text-anchor="middle"
                  class="pointer-events-none font-sans text-[10px] font-medium"
                  :fill="zoneColors['occiput-l']"
                  opacity="0.75"
                >
                  occiput L
                </text>
                <text
                  x="570"
                  y="216"
                  text-anchor="middle"
                  class="pointer-events-none font-sans text-[10px] font-medium"
                  :fill="zoneColors['occiput-r']"
                  opacity="0.75"
                >
                  occiput R
                </text>
                <text
                  x="510"
                  y="296"
                  text-anchor="middle"
                  class="pointer-events-none font-sans text-[11px] font-medium"
                  :fill="zoneColors['base']"
                  opacity="0.75"
                >
                  base of skull
                </text>
                <text
                  x="510"
                  y="358"
                  text-anchor="middle"
                  class="pointer-events-none font-sans text-[11px] font-medium"
                  :fill="zoneColors['neck']"
                  opacity="0.75"
                >
                  neck
                </text>
              </svg>

              <div class="mt-4 flex flex-wrap justify-center gap-2">
                <div
                  v-for="(label, id) in zoneLabels"
                  :key="id"
                  class="flex cursor-pointer items-center gap-2 rounded-full px-3 py-1 text-xs transition-colors"
                  :class="
                    isZoneActive(id as string)
                      ? 'bg-white/40 font-semibold text-gray-600'
                      : 'bg-white/10 text-gray-600/70'
                  "
                  @click="toggleZone(id as string)"
                >
                  <div
                    class="h-2 w-2 rounded-full"
                    :style="{ backgroundColor: zoneColors[id] }"
                  ></div>
                  {{ label }}
                </div>
              </div>

              <p v-if="selectedZones.size > 0" class="mt-4 text-sm text-gray-600">
                <strong class="font-bold">Pain areas:</strong>
                {{
                  Array.from(selectedZones)
                    .map((id) => zoneLabels[id])
                    .join(', ')
                }}
              </p>
              <p v-else class="mt-4 text-sm text-gray-600">
                Tap a region on either head to mark where it hurts
              </p>

              <button
                v-if="selectedZones.size > 0"
                class="mt-2 text-xs text-gray-600 underline"
                @click="clearSelection"
              >
                Clear all
              </button>
            </div>
          </div>
          <div class="my-6 rounded-2xl bg-[#ffffff44] p-6 shadow-sm">
            <h2 class="font-fredoka font-outline-1 mb-4 text-center text-xl text-white">
              Intensity: {{ currentIntensity }}
            </h2>
            <div class="flex items-center space-x-4">
              <span class="text-2xl">😊</span>
              <input
                v-model.number="currentIntensity"
                type="range"
                min="1"
                max="10"
                class="h-2 w-full cursor-pointer appearance-none rounded-lg bg-gray-200"
              />
              <span class="text-2xl">😫</span>
            </div>
            <div class="mt-6">
              <textarea
                v-model="currentNote"
                placeholder="Add a note (symptoms, triggers...)"
                class="font-quicksand w-full resize-none rounded-xl border border-[#d8b99b] bg-[#fff7ef] p-4 text-base text-[#5c3d2e] shadow-inner focus:ring-2 focus:ring-[#caa37b] focus:outline-none"
                rows="3"
              ></textarea>
            </div>

            <button
              :disabled="isLoading || selectedZones.size === 0"
              class="bg-fill-positive hover:bg-bg-positive mt-6 w-full cursor-pointer rounded-xl py-3 font-bold text-white transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50"
              @click="saveHeadache"
            >
              {{ isLoading ? 'Saving...' : 'Track Headache' }}
            </button>
          </div>

          <div class="grid grid-cols-1 gap-8 lg:grid-cols-2">
            <!-- Left Column: Input -->
            <div class="space-y-6">
              <div class="rounded-2xl bg-[#ffffff44] p-6 shadow-sm">
                <h2 class="font-fredoka font-outline-1 mb-4 text-center text-xl text-white">
                  Headache Heatmap
                </h2>
                <div class="flex flex-col items-center">
                  <svg
                    id="heatmap-svg"
                    width="100%"
                    viewBox="0 0 680 400"
                    role="img"
                    class="max-w-[400px]"
                  >
                    <title>Headache heatmap</title>
                    <!-- FRONT HEAD -->
                    <path
                      d="M148 328 C146 345 143 360 141 375 L199 375 C197 360 194 345 192 328 Z"
                      fill="#e8d5c4"
                      stroke="#c4a882"
                      stroke-width="0.5"
                      opacity="0.5"
                    />
                    <ellipse
                      cx="102"
                      cy="238"
                      rx="13"
                      ry="20"
                      fill="#e8d5c4"
                      stroke="#c4a882"
                      stroke-width="0.5"
                      opacity="0.5"
                    />
                    <ellipse
                      cx="238"
                      cy="238"
                      rx="13"
                      ry="20"
                      fill="#e8d5c4"
                      stroke="#c4a882"
                      stroke-width="0.5"
                      opacity="0.5"
                    />
                    <path
                      d="M118 310 C104 305 96 286 95 270 L92 196 C90 138 110 90 170 83 C230 90 250 138 248 196 L245 270 C244 286 236 305 222 310 Z"
                      fill="#f5e6d8"
                      stroke="#c4a882"
                      stroke-width="0.8"
                      opacity="0.5"
                    />

                    <!-- FRONT ZONES HEATMAP -->
                    <path
                      d="M128 97 C138 82 158 78 170 77 C182 78 202 82 212 97 L218 135 L122 135 Z"
                      :fill="zoneColors['crown']"
                      :fill-opacity="getHeatmapOpacity('crown')"
                    />
                    <rect
                      x="122"
                      y="135"
                      width="96"
                      height="55"
                      rx="3"
                      :fill="zoneColors['forehead']"
                      :fill-opacity="getHeatmapOpacity('forehead')"
                    />
                    <path
                      d="M122 135 L90 150 L92 215 L108 222 L118 190 L122 190 Z"
                      :fill="zoneColors['temple-l']"
                      :fill-opacity="getHeatmapOpacity('temple-l')"
                    />
                    <path
                      d="M218 135 L250 150 L248 215 L232 222 L222 190 L218 190 Z"
                      :fill="zoneColors['temple-r']"
                      :fill-opacity="getHeatmapOpacity('temple-r')"
                    />
                    <path
                      d="M122 190 L118 190 L108 222 L120 244 L140 247 L147 222 L143 190 Z"
                      :fill="zoneColors['sinus-l']"
                      :fill-opacity="getHeatmapOpacity('sinus-l')"
                    />
                    <path
                      d="M218 190 L222 190 L232 222 L220 244 L200 247 L193 222 L197 190 Z"
                      :fill="zoneColors['sinus-r']"
                      :fill-opacity="getHeatmapOpacity('sinus-r')"
                    />
                    <path
                      d="M125 116 L106 142 L122 135 Z"
                      :fill="zoneColors['crown-temple-l']"
                      :fill-opacity="getHeatmapOpacity('crown-temple-l')"
                    />
                    <path
                      d="M215 116 L234 142 L218 135 Z"
                      :fill="zoneColors['crown-temple-r']"
                      :fill-opacity="getHeatmapOpacity('crown-temple-r')"
                    />

                    <!-- BACK HEAD -->
                    <path
                      d="M488 328 C486 345 483 360 481 375 L539 375 C537 360 534 345 532 328 Z"
                      fill="#e8d5c4"
                      stroke="#c4a882"
                      stroke-width="0.5"
                      opacity="0.5"
                    />
                    <ellipse
                      cx="442"
                      cy="238"
                      rx="13"
                      ry="20"
                      fill="#e8d5c4"
                      stroke="#c4a882"
                      stroke-width="0.5"
                      opacity="0.5"
                    />
                    <ellipse
                      cx="578"
                      cy="238"
                      rx="13"
                      ry="20"
                      fill="#e8d5c4"
                      stroke="#c4a882"
                      stroke-width="0.5"
                      opacity="0.5"
                    />
                    <path
                      d="M458 310 C444 305 436 286 435 270 L432 196 C430 138 450 88 510 82 C570 88 590 138 588 196 L585 270 C584 286 576 305 562 310 Z"
                      fill="#edddd0"
                      stroke="#c4a882"
                      stroke-width="0.8"
                      opacity="0.5"
                    />

                    <!-- BACK ZONES HEATMAP -->
                    <path
                      d="M458 100 C470 84 492 78 510 77 C528 78 550 84 562 100 L568 160 L452 160 Z"
                      :fill="zoneColors['occiput-top']"
                      :fill-opacity="getHeatmapOpacity('occiput-top')"
                    />
                    <path
                      d="M452 160 L434 175 L432 250 L450 265 L472 260 L480 200 L480 160 Z"
                      :fill="zoneColors['occiput-l']"
                      :fill-opacity="getHeatmapOpacity('occiput-l')"
                    />
                    <path
                      d="M568 160 L586 175 L588 250 L570 265 L548 260 L540 200 L540 160 Z"
                      :fill="zoneColors['occiput-r']"
                      :fill-opacity="getHeatmapOpacity('occiput-r')"
                    />
                    <path
                      d="M480 260 L450 265 L432 280 L435 310 L458 325 L488 335 L510 338 L532 335 L562 325 L585 310 L588 280 L570 265 L548 260 L540 200 L480 200 Z"
                      :fill="zoneColors['base']"
                      :fill-opacity="getHeatmapOpacity('base')"
                    />
                    <path
                      d="M488 328 C486 345 483 360 481 375 L539 375 C537 360 534 345 532 328 Z"
                      :fill="zoneColors['neck']"
                      :fill-opacity="getHeatmapOpacity('neck')"
                    />
                  </svg>
                </div>
                <p class="mt-2 text-center text-sm text-gray-600">
                  Visualization of your recurring pain areas
                </p>
              </div>
            </div>

            <!-- Right Column: Heatmap & History -->
            <div class="space-y-6">
              <div v-if="headaches.length > 0" class="space-y-4">
                <h3 class="font-fredoka font-outline-1 text-center text-3xl text-white">History</h3>
                <div class="max-h-[400px] space-y-3 overflow-y-auto pr-2">
                  <div v-for="h in headaches" :key="h.id" class="rounded-xl bg-[#ffffff44] p-4">
                    <div class="flex items-center justify-between">
                      <div class="flex items-center">
                        <div
                          class="mr-3 flex h-10 w-10 items-center justify-center rounded-full font-bold text-white"
                          :style="{ backgroundColor: getIntensityColor(h.intensity) }"
                        >
                          {{ h.intensity }}
                        </div>
                        <div>
                          <div class="text-sm text-gray-600">
                            {{ new Date(h.createdAt).toLocaleString() }}
                          </div>
                          <div class="text-xs text-gray-500">
                            {{ h.zones.map((z) => zoneLabels[z]).join(', ') }}
                          </div>
                        </div>
                      </div>
                      <button
                        :disabled="isLoading"
                        class="text-gray-500 transition-colors hover:text-red-500 disabled:cursor-not-allowed disabled:opacity-50"
                        @click="() => deleteHeadache(h.id)"
                      >
                        <Trash2 class="h-5 w-5" />
                      </button>
                    </div>
                    <div v-if="h.note" class="mt-2 text-sm text-gray-700 italic">
                      "{{ h.note }}"
                    </div>
                  </div>
                </div>
              </div>

              <div v-else-if="!isLoading" class="py-12 text-center text-gray-600">
                <div class="mb-2 text-4xl">🤕</div>
                <p class="f">No headache records yet.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { IonContent, IonPage } from '@ionic/vue';
import RoundHeader from '../../components/RoundHeader.vue';
import { Trash2 } from 'lucide-vue-next';
import { Headache } from '../../models/Headache.ts';
import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  getFirestore,
  type DocumentData,
} from 'firebase/firestore';
import { useCurrentUser, useCollection } from 'vuefire';

const db = getFirestore();
const user = useCurrentUser();

const selectedZones = ref<Set<string>>(new Set());
const currentIntensity = ref(5);
const currentNote = ref('');
const error = ref<string | null>(null);

const zoneColors: Record<string, string> = {
  crown: '#FF5722',
  forehead: '#FF9800',
  'temple-l': '#FFC107',
  'temple-r': '#FFC107',
  'sinus-l': '#4CAF50',
  'sinus-r': '#4CAF50',
  'crown-temple-l': '#FF7043',
  'crown-temple-r': '#FF7043',
  'occiput-top': '#9C27B0',
  'occiput-l': '#673AB7',
  'occiput-r': '#673AB7',
  base: '#3F51B5',
  neck: '#2196F3',
};

const zoneLabels: Record<string, string> = {
  crown: 'Crown',
  forehead: 'Forehead',
  'temple-l': 'Left Temple',
  'temple-r': 'Right Temple',
  'sinus-l': 'Left Sinus',
  'sinus-r': 'Right Sinus',
  'crown-temple-l': 'Upper Left Side',
  'crown-temple-r': 'Upper Right Side',
  'occiput-top': 'Upper Back',
  'occiput-l': 'Left Occiput',
  'occiput-r': 'Right Occiput',
  base: 'Base of Skull',
  neck: 'Neck',
};

function isZoneActive(id: string) {
  return selectedZones.value.has(id);
}

function toggleZone(id: string) {
  if (selectedZones.value.has(id)) {
    selectedZones.value.delete(id);
  } else {
    selectedZones.value.add(id);
  }
}

function clearSelection() {
  selectedZones.value.clear();
}

function getHeatmapOpacity(zoneId: string) {
  if (!headaches.value.length) return 0.1;
  const count = headaches.value.filter((h) => h.zones && h.zones.includes(zoneId)).length;
  return Math.min(0.1 + count / 5, 0.8);
}

const headachesCollection = useCollection(() =>
  user.value ? collection(db, `users/${user.value.uid}/headaches`) : null
);

const headaches = computed(() => {
  if (!headachesCollection.value) return [];
  return (headachesCollection.value as DocumentData[])
    .map((data) => {
      const h = new Headache();
      Object.assign(h, data);
      return h;
    })
    .sort((a, b) => b.createdAt - a.createdAt);
});

const isLoading = computed(() => !user.value || headachesCollection.value === undefined);

async function saveHeadache() {
  if (!user.value || selectedZones.value.size === 0) return;

  error.value = null;
  try {
    const h = new Headache();
    h.intensity = currentIntensity.value;
    h.zones = Array.from(selectedZones.value);
    h.note = currentNote.value;

    const ref = doc(db, `users/${user.value.uid}/headaches/${h.id}`);
    await setDoc(ref, h.toPlain());

    selectedZones.value.clear();
    currentIntensity.value = 5;
    currentNote.value = '';
  } catch (err) {
    console.error('Error saving headache:', err);
    error.value = 'Failed to save record.';
  }
}

async function deleteHeadache(id: string) {
  if (!user.value) return;
  try {
    await deleteDoc(doc(db, `users/${user.value.uid}/headaches/${id}`));
  } catch (err) {
    console.error('Error deleting headache:', err);
  }
}

function getIntensityColor(intensity: number) {
  if (intensity <= 3) return '#8BC34A';
  if (intensity <= 6) return '#FFC107';
  if (intensity <= 8) return '#FF9800';
  return '#F44336';
}
</script>

<style scoped>
input[type='range']::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 24px;
  height: 24px;
  background: #ffffff;
  border: 2px solid #caa37b;
  border-radius: 50%;
  cursor: pointer;
}

input[type='range']::-moz-range-thumb {
  width: 24px;
  height: 24px;
  background: #ffffff;
  border: 2px solid #caa37b;
  border-radius: 50%;
  cursor: pointer;
}
</style>
